import { StatementRecord } from '../statementServices';
import { PdfExtractor } from '../../utils/pdfExtractor';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import objectHash from 'object-hash';

dayjs.extend(customParseFormat);

export async function parseOzonStatement(file: File): Promise<StatementRecord[]> {
    const extractor = new PdfExtractor(file);
    const text = await extractor.extractFullText();

    // Split by lines is risky because PdfExtractor joins with spaces, but let's try to match the pattern in the blob.
    // The PdfExtractor logic: returns `fullText` with `\n--- Page X ---\n`.
    // But inside a page, `pageText` is `items.join(' ')`. This is bad for tables.
    // Ideally we want NEWLINES between visually distinct vertical items, but `pdfjs-dist` textContent provides items in reading order.
    // If it joins with spaces, "Date" "Doc" "Desc" "Amount" become one line.

    // Pattern: 
    // Date: DD.MM.YYYY HH:mm:ss
    // Doc: \d+
    // Desc: ...
    // Amount: [+-] RUR X.XX

    // Regex explanation:
    // 1. Date: (\d{2}\.\d{2}\.\d{4}\s\d{2}:\d{2}:\d{2})
    // 2. Space
    // 3. Doc: (\d+)
    // 4. Space
    // 5. Desc: (.+?)  (Non-greedy, match until Amount)
    // 6. Amount: ([+-]\s*RUR\s*[\d\s]+\.\d{2})

    // Note: The spaces in amount "1 000.00" need to be handled.

    const regex = /(\d{2}\.\d{2}\.\d{4}\s\d{2}:\d{2}:\d{2})\s+(\d+)\s+(.+?)\s+([+-]\s*RUR\s*[\d\s]+\.\d{2})/g;

    const records: StatementRecord[] = [];
    let match;

    while ((match = regex.exec(text)) !== null) {
        const [_, dateStr, docId, desc, amountStr] = match;

        // Parse Amount
        // Remove "RUR" and spaces, keep Sign
        const cleanAmountStr = amountStr.replace(/RUR/g, '').replace(/\s/g, '');
        const amount = parseFloat(cleanAmountStr);

        // Parse Date
        // Format: 18.01.2026 20:25:29
        const date = dayjs(dateStr, 'DD.MM.YYYY HH:mm:ss').toISOString();

        // Categorize based on description (Basic rules)
        let category: string = 'EXPENSE';
        let subCategory = 'General';

        const lowerDesc = desc.toLowerCase();
        const isTransfer = lowerDesc.includes('transfer') || lowerDesc.includes('sbp') || lowerDesc.includes('replenishment') || lowerDesc.includes('perevod'); // perevod = transfer in russian translit sometimes

        if (amount > 0) {
            if (isTransfer) {
                category = 'TRANSFERS';
                subCategory = 'Incoming Transfer';
            } else {
                category = 'INCOME';
                subCategory = 'Income';
            }
        } else {
            // Expenses
            if (isTransfer) {
                category = 'TRANSFERS';
                subCategory = 'Outgoing Transfer';
            }
            else if (lowerDesc.includes('ozon')) subCategory = 'Shopping';
        }

        const record: StatementRecord = {
            id: objectHash({ date, amount, desc, docId }), // Deterministic ID
            date,
            amount,
            currency: 'RUB',
            description: desc.trim(),
            card: 0, // Not available in statement
            hash: objectHash({ date, amount, desc }), // Duplicate check hash
            subCategory,
            category,
            subCategoryId: '', // Will be filled by `uploadStatementRecords` mapping
        };

        records.push(record);
    }

    console.log(`[OzonParser] Extracted ${records.length} records.`);
    return records;
}
